import React from 'react';
import { Box } from '@mui/material';
import theme from 'theme/theme';
import { Model } from '../../hooks/useModelState';
import { PCNode } from '../../MineModel/Nodes';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { SceneProps } from '../scene-utils';
import { Scene } from '../types';
import sideDrawerContentFunction from './BulkImportSolidsSceneSideDrawer';
import ModelDrawerTrigger from '../../ModelDrawerTrigger';
import getBulkImportSolidsSceneActions from './BulkImportSolidsSceneActions';
import {
  subscribeToBulkImportSolidsSceneState,
  getBulkImportSolidsSceneState,
  updateBulkImportSolidsSceneState,
  decisionsWithNodeApplied,
  getCurrentSolid,
  getSolidName
} from './bulkImportSolidsSceneState';
import BulkImportSolidsOverlay from './BulkImportSolidsOverlay';
import { IntersectionData } from '../../MineModel/intersectionTypes';

const TITLE = 'BULK IMPORT SOLIDS';

const BulkImportSolidsDisplay = ({ props }: { props: SceneProps }) => {
  const [, forceUpdate] = React.useReducer((x) => x + 1, 0);

  const sceneState = getBulkImportSolidsSceneState();
  const currentSolid = getCurrentSolid();

  React.useEffect(() => {
    const unsubscribe = subscribeToBulkImportSolidsSceneState(() => forceUpdate());
    return unsubscribe;
  }, []);

  return (
    <>
      {!props.context.state.drawerOpen && (
        <Box sx={{ position: 'absolute', right: theme.spacing(2), zIndex: 20 }}>
          <ModelDrawerTrigger handleClick={() => props.context.actions.setDrawerOpen(true)} />
        </Box>
      )}
      {sceneState.wizardPhase === 'manual_review' && currentSolid && (
        <BulkImportSolidsOverlay
          currentIndex={sceneState.currentManualReviewIndex}
          totalSolids={sceneState.manualReviewQueue.length}
          currentSolidName={getSolidName(currentSolid)}
          drawerOpen={props.context.state.drawerOpen}
        />
      )}
    </>
  );
};

const BulkImportSolidsScene = (props: SceneProps): Scene => {
  const display = () => <BulkImportSolidsDisplay props={props} />;

  const getCanvasHandlers = (model: Model) => ({
    onNodeHover: () => { },
    onNodeSelect: (node: PCNode) => {
      model.setSelectedNode(node);
      updateBulkImportSolidsSceneState({ selectedNodeId: node.id, spoolClickInfo: null, ...decisionsWithNodeApplied(node.id) });
    },
    onSpoolSelect: (spool: PCSpool, clickPoint?: { x: number; y: number; z: number }) => {
      const state = getBulkImportSolidsSceneState();
      if (!state.isSelectingSpoolForInsert) return;
      if (clickPoint && spool.pointA?.id && spool.pointB?.id) {
        updateBulkImportSolidsSceneState({
          spoolClickInfo: {
            spoolId: spool.spoolId,
            nodeAId: spool.pointA.id,
            nodeBId: spool.pointB.id,
            clickPoint,
          },
          isSelectingSpoolForInsert: false,
        });
      }
    },
    onIntersections: (intersections: IntersectionData[]) => {
      const nodeHit = intersections.find((i) => i.type === 'node');
      if (nodeHit) {
        const node = nodeHit.node ?? model.findNodeById(nodeHit.nodeId!);
        if (node) {
          model.setSelectedNode(node);
          updateBulkImportSolidsSceneState({ selectedNodeId: node.id, spoolClickInfo: null, ...decisionsWithNodeApplied(node.id) });
          return;
        }
      }
      const currentState = getBulkImportSolidsSceneState();
      if (currentState.isSelectingSpoolForInsert) {
        const spoolHit = intersections.find((i) => i.type === 'spool');
        if (spoolHit?.spoolId && spoolHit.clickPoint) {
          const spool = model.spools[spoolHit.spoolId];
          if (spool?.pointA?.id && spool?.pointB?.id) {
            updateBulkImportSolidsSceneState({
              spoolClickInfo: {
                spoolId: spoolHit.spoolId,
                nodeAId: spool.pointA.id,
                nodeBId: spool.pointB.id,
                clickPoint: spoolHit.clickPoint,
              },
              isSelectingSpoolForInsert: false,
            });
          }
        }
      }
    },
  });

  const sideDrawerContent = (model: Model) => sideDrawerContentFunction(props, model);

  return {
    key: props.key,
    display,
    getCanvasHandlers,
    sideDrawerContent,
    title: TITLE,
    actionButtons: getBulkImportSolidsSceneActions(),
  };
};

export default BulkImportSolidsScene;
